import { onMounted, onUnmounted, ref, type Ref } from 'vue';
import type { MavePlayerComponentInstance } from './mave-video';

const MAVE_PLAYER_VIDEO_BIND_RETRY_MS = 500;
const MAVE_PLAYER_VIDEO_BIND_MAX_RETRIES = 6;
const MAVE_PLAYER_READY_FALLBACK_MS = 10000;
const VIDEO_HAVE_CURRENT_DATA = 2;
const TEACHINO_MAVE_MOBILE_SUBTITLE_STYLE_ID =
  'teachino-mave-mobile-subtitle-styles';
/** Matches Tailwind `md` breakpoint minus one pixel. */
const MOBILE_LAYOUT_MAX_WIDTH_PX = '767';
const MOBILE_SUBTITLE_FONT_SIZE = '0.75rem';
const MOBILE_SUBTITLE_LINE_HEIGHT = '1.25';
const MOBILE_SUBTITLE_PADDING = '2px 8px 4px';

function buildCompactMobileSubtitleCss(): string {
  return `@media (max-width: ${MOBILE_LAYOUT_MAX_WIDTH_PX}px), (max-device-width: ${MOBILE_LAYOUT_MAX_WIDTH_PX}px) {
  .subtitles > div,
  #subtitles_text {
    font-size: ${MOBILE_SUBTITLE_FONT_SIZE} !important;
    line-height: ${MOBILE_SUBTITLE_LINE_HEIGHT} !important;
    min-height: auto;
    padding: ${MOBILE_SUBTITLE_PADDING};
  }
  video::cue {
    font-size: ${MOBILE_SUBTITLE_FONT_SIZE};
    line-height: ${MOBILE_SUBTITLE_LINE_HEIGHT};
  }
}`;
}

function injectCompactMobileSubtitleStylesIntoShadow(
  shadowRoot: ShadowRoot,
): void {
  if (shadowRoot.getElementById(TEACHINO_MAVE_MOBILE_SUBTITLE_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = TEACHINO_MAVE_MOBILE_SUBTITLE_STYLE_ID;
  style.textContent = buildCompactMobileSubtitleCss();
  shadowRoot.appendChild(style);
}

function injectCompactMobileSubtitleStyles(
  playerRef: Ref<MavePlayerRefValue>,
): void {
  const playerElement = resolvePlayerElement(playerRef);
  if (!playerElement?.shadowRoot) {
    return;
  }

  injectCompactMobileSubtitleStylesIntoShadow(playerElement.shadowRoot);

  for (const element of playerElement.shadowRoot.querySelectorAll('*')) {
    if (element.shadowRoot) {
      injectCompactMobileSubtitleStylesIntoShadow(element.shadowRoot);
    }
  }
}

function tryInjectCompactMobileSubtitleStyles(
  playerRef: Ref<MavePlayerRefValue>,
  compactSubtitlesOnMobile: boolean,
): void {
  if (!compactSubtitlesOnMobile) {
    return;
  }

  injectCompactMobileSubtitleStyles(playerRef);
}

type MavePlayerRefValue = MavePlayerComponentInstance | HTMLElement | null;

type MavePlayerVideoStateOptions = {
  compactSubtitlesOnMobile?: boolean;
};

function resolvePlayerElement(
  playerRef: Ref<MavePlayerRefValue>,
): HTMLElement | null {
  const value = playerRef.value;
  if (!value) {
    return null;
  }

  if (value instanceof HTMLElement) {
    return value;
  }

  return value.element;
}

function isVideoSoundEnabled(
  playerRef: Ref<MavePlayerRefValue>,
  video: HTMLVideoElement | null,
): boolean {
  if (video && !video.muted && video.volume > 0) {
    return true;
  }

  const player = playerRef.value;
  if (player && !(player instanceof HTMLElement)) {
    return !player.muted;
  }

  return false;
}

function isPlayerElementRunning(
  playerRef: Ref<MavePlayerRefValue>,
  video: HTMLVideoElement | null,
): boolean {
  const player = playerRef.value;
  if (player && !(player instanceof HTMLElement)) {
    if (video?.ended) {
      return false;
    }

    return !player.paused;
  }

  return video ? !video.paused && !video.ended : false;
}

function findVideoElement(
  playerRef: Ref<MavePlayerRefValue>,
): HTMLVideoElement | null {
  const playerElement = resolvePlayerElement(playerRef);
  if (!playerElement) {
    return null;
  }

  const shadowVideo = playerElement.shadowRoot?.querySelector('video');
  if (shadowVideo) {
    return shadowVideo;
  }

  return playerElement.querySelector('video');
}

export function useMavePlayerVideoState(
  playerRef: Ref<MavePlayerRefValue>,
  options: MavePlayerVideoStateOptions = {},
) {
  const isVideoRunning = ref(false);
  const hasUserEnabledSound = ref(false);
  const hasVideoEnded = ref(false);
  const isPlayerReady = ref(false);
  const cleanupFns: (() => void)[] = [];
  let observer: MutationObserver | null = null;
  let boundVideo: HTMLVideoElement | null = null;
  let readyFallbackTimeoutId: number | undefined;

  const markPlayerReady = (): void => {
    if (isPlayerReady.value) {
      return;
    }

    isPlayerReady.value = true;
    if (readyFallbackTimeoutId !== undefined) {
      window.clearTimeout(readyFallbackTimeoutId);
      readyFallbackTimeoutId = undefined;
    }
  };

  const scheduleReadyFallback = (): void => {
    if (readyFallbackTimeoutId !== undefined) {
      return;
    }

    readyFallbackTimeoutId = window.setTimeout(() => {
      markPlayerReady();
    }, MAVE_PLAYER_READY_FALLBACK_MS);
  };

  const refreshSoundState = (): void => {
    if (hasUserEnabledSound.value) {
      return;
    }

    const video = findVideoElement(playerRef);
    if (isVideoSoundEnabled(playerRef, video)) {
      hasUserEnabledSound.value = true;
    }
  };

  const refresh = (): void => {
    const video = findVideoElement(playerRef);
    isVideoRunning.value = isPlayerElementRunning(playerRef, video);
    refreshSoundState();
  };

  const bindToVideo = (video: HTMLVideoElement): void => {
    if (boundVideo === video) {
      refresh();
      return;
    }

    boundVideo = video;

    const onPlay = (): void => {
      isVideoRunning.value = true;
    };
    const onStop = (): void => {
      refresh();
    };
    const onEnded = (): void => {
      isVideoRunning.value = false;
      hasVideoEnded.value = true;
    };
    const onVolumeChange = (): void => {
      refreshSoundState();
    };
    const onPlayerReady = (): void => {
      markPlayerReady();
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('playing', onPlay);
    video.addEventListener('pause', onStop);
    video.addEventListener('ended', onEnded);
    video.addEventListener('volumechange', onVolumeChange);
    video.addEventListener('loadeddata', onPlayerReady);
    video.addEventListener('canplay', onPlayerReady);

    cleanupFns.push(() => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('playing', onPlay);
      video.removeEventListener('pause', onStop);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('loadeddata', onPlayerReady);
      video.removeEventListener('canplay', onPlayerReady);
    });

    if (video.readyState >= VIDEO_HAVE_CURRENT_DATA) {
      markPlayerReady();
    }

    refresh();
  };

  const tryBind = (): boolean => {
    const playerElement = resolvePlayerElement(playerRef);
    if (!playerElement) {
      return false;
    }

    tryInjectCompactMobileSubtitleStyles(
      playerRef,
      options.compactSubtitlesOnMobile ?? false,
    );

    const video = findVideoElement(playerRef);
    if (!video) {
      return false;
    }

    bindToVideo(video);
    return true;
  };

  const setup = (): void => {
    if (tryBind()) {
      return;
    }

    const playerElement = resolvePlayerElement(playerRef);
    if (playerElement?.shadowRoot) {
      tryInjectCompactMobileSubtitleStyles(
        playerRef,
        options.compactSubtitlesOnMobile ?? false,
      );
      observer = new MutationObserver(() => {
        tryInjectCompactMobileSubtitleStyles(
          playerRef,
          options.compactSubtitlesOnMobile ?? false,
        );
        if (tryBind()) {
          observer?.disconnect();
          observer = null;
        }
      });
      observer.observe(playerElement.shadowRoot, {
        childList: true,
        subtree: true,
      });
      cleanupFns.push(() => {
        observer?.disconnect();
        observer = null;
      });
    }

    let retries = 0;
    const intervalId = window.setInterval(() => {
      retries++;
      if (tryBind() || retries >= MAVE_PLAYER_VIDEO_BIND_MAX_RETRIES) {
        window.clearInterval(intervalId);
      }
    }, MAVE_PLAYER_VIDEO_BIND_RETRY_MS);
    cleanupFns.push(() => window.clearInterval(intervalId));
  };

  onMounted(() => {
    scheduleReadyFallback();
    setup();
  });

  onUnmounted(() => {
    if (readyFallbackTimeoutId !== undefined) {
      window.clearTimeout(readyFallbackTimeoutId);
    }
    for (const cleanup of cleanupFns) {
      cleanup();
    }
    boundVideo = null;
  });

  return {
    isVideoRunning,
    hasUserEnabledSound,
    hasVideoEnded,
    isPlayerReady,
    refresh,
  };
}
